import * as PIXI from 'pixi.js';
import { Vector2 } from './Vector2';

let nextId = 0;

export abstract class Entity {
  readonly id: number;
  readonly position = new Vector2();
  readonly velocity = new Vector2();
  readonly sprite: PIXI.Container;
  active = true;
  radius = 16;

  constructor() {
    this.id = nextId++;
    this.sprite = new PIXI.Container();
    this.sprite.label = `entity_${this.id}`;
  }

  abstract update(dt: number, tick: number): void;

  destroy(): void {
    this.active = false;
    if (this.sprite.parent) {
      this.sprite.parent.removeChild(this.sprite);
    }
    this.sprite.destroy({ children: true });
  }
}

export class EntityManager {
  private entities = new Map<number, Entity>();

  add(entity: Entity): void {
    this.entities.set(entity.id, entity);
  }

  remove(id: number): void {
    const entity = this.entities.get(id);
    if (entity) {
      entity.destroy();
      this.entities.delete(id);
    }
  }

  get(id: number): Entity | undefined {
    return this.entities.get(id);
  }

  getAll(): Entity[] {
    return Array.from(this.entities.values()).filter((e) => e.active);
  }

  getByType<T extends Entity>(type: new (...args: unknown[]) => T): T[] {
    return this.getAll().filter((e): e is T => e instanceof type);
  }

  update(dt: number, tick: number): void {
    for (const entity of this.entities.values()) {
      if (entity.active) {
        entity.update(dt, tick);
      }
    }
  }

  clear(): void {
    for (const entity of this.entities.values()) {
      entity.destroy();
    }
    this.entities.clear();
  }

  get count(): number {
    return this.entities.size;
  }
}
